import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { X, Send, CheckCircle } from "lucide-react";
import { submitInquiry } from "@/lib/api";
import { toast } from "sonner";

const InquiryCtx = createContext(null);

export function useInquiryForm() {
  return useContext(InquiryCtx);
}

export function InquiryFormProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState({});

  const openForm = useCallback((data = {}) => {
    setPrefill(data);
    setOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setOpen(false);
    setPrefill({});
  }, []);

  const value = useMemo(() => ({ openForm, closeForm }), [openForm, closeForm]);

  return (
    <InquiryCtx.Provider value={value}>
      {children}
      {open && <InquiryModal prefill={prefill} onClose={closeForm} />}
    </InquiryCtx.Provider>
  );
}

function InquiryModal({ prefill, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", travelers: 1,
    preferred_date: "", message: prefill.message || "",
    trip_slug: prefill.tripSlug || "", ...prefill.utm,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitInquiry(form);
      setSubmitted(true);
      toast.success("Inquiry submitted! We'll reach out soon.");
    } catch (err) {
      console.error("Inquiry submission failed:", err.message);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl border border-earth-200 font-body text-sm text-earth-900 bg-white placeholder:text-earth-400 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition-all";
  const labelClass = "block font-body text-xs font-medium text-earth-600 mb-1.5";

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="inquiry-popup">
      <div className="absolute inset-0 bg-night/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom" data-testid="inquiry-popup-modal">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 sm:px-6 pt-5 pb-3 border-b border-earth-100">
          <div>
            <h3 className="font-heading font-bold text-lg text-earth-900">
              {prefill.title || "Get a Quote"}
            </h3>
            {prefill.tripSlug && (
              <p className="font-body text-xs text-brand mt-0.5">{prefill.tripSlug.replace(/-/g, " ")}</p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-earth-100 transition-colors" data-testid="close-inquiry-popup">
            <X className="w-4 h-4 text-earth-700" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center" data-testid="inquiry-popup-success">
            <CheckCircle className="w-14 h-14 text-brand mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-earth-900 mb-2">Request Received!</h3>
            <p className="font-body text-sm text-earth-700 mb-6">Our travel expert will reach out within 2 hours.</p>
            <button onClick={onClose} className="bg-brand hover:bg-brand-hover text-white font-heading font-semibold text-sm px-6 py-3 rounded-full transition-all active:scale-95">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 sm:px-6 py-5 space-y-4" data-testid="inquiry-popup-form">
            <div>
              <label className={labelClass}>Full Name *</label>
              <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rahul Sharma" className={inputClass} data-testid="popup-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Phone *</label>
                <input type="tel" required value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 98765 43210" className={inputClass} data-testid="popup-phone" />
              </div>
              <div>
                <label className={labelClass}>Email *</label>
                <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@email.com" className={inputClass} data-testid="popup-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>No. of Travelers</label>
                <input type="number" min="1" value={form.travelers} onChange={e => setForm(f => ({ ...f, travelers: parseInt(e.target.value) || 1 }))} placeholder="2" className={inputClass} data-testid="popup-travelers" />
              </div>
              <div>
                <label className={labelClass}>Preferred Date</label>
                <input type="date" value={form.preferred_date} onChange={e => setForm(f => ({ ...f, preferred_date: e.target.value }))} className={inputClass} data-testid="popup-date" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Message</label>
              <textarea rows="3" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Tell us about your dream trip — destinations, budget, group size..." className={`${inputClass} resize-none`} data-testid="popup-message" />
            </div>
            <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-heading font-semibold py-3.5 rounded-full transition-all active:scale-95 disabled:opacity-60" data-testid="popup-submit">
              <Send className="w-4 h-4" /> {submitting ? "Submitting..." : "Submit Inquiry"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
